// src/components/shared/DataTable.jsx
import React, { useState } from 'react';
import LoadingState from './LoadingState';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Checkbox,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';

const DataTable = ({
  columns,
  data,
  onRowClick,
  onRowSelect,
  actions,
  loading,
  error,
  selectable = false,
  pagination = true,
  stickyHeader = true,
  size = "small",
  emptyMessage = "No hay datos para mostrar"
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedRows, setSelectedRows] = useState([]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelected = data.map((row) => row.id);
      setSelectedRows(newSelected);
      onRowSelect && onRowSelect(newSelected);
    } else {
      setSelectedRows([]);
      onRowSelect && onRowSelect([]);
    }
  };

  const handleSelectRow = (id) => {
    const selectedIndex = selectedRows.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedRows, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedRows.slice(1));
    } else if (selectedIndex === selectedRows.length - 1) {
      newSelected = newSelected.concat(selectedRows.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedRows.slice(0, selectedIndex),
        selectedRows.slice(selectedIndex + 1),
      );
    }

    setSelectedRows(newSelected);
    onRowSelect && onRowSelect(newSelected);
  };

  const isSelected = (id) => selectedRows.indexOf(id) !== -1;

  if (loading) return <LoadingState loading={true} />;
  if (error) return <LoadingState error={error} />;

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table stickyHeader={stickyHeader} size={size}>
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedRows.length > 0 && selectedRows.length < data.length}
                    checked={data.length > 0 && selectedRows.length === data.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.field}
                  align={column.align || 'left'}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.headerName}
                </TableCell>
              ))}
              {actions && <TableCell align="center">Acciones</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + (selectable ? 1 : 0) + (actions ? 1 : 0)} align="center">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              (pagination ? data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : data)
                .map((row) => {
                  const isItemSelected = isSelected(row.id);

                  return (
                    <TableRow
                      hover
                      onClick={() => onRowClick && onRowClick(row)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.id}
                      selected={isItemSelected}
                    >
                      {selectable && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isItemSelected}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleSelectRow(row.id);
                            }}
                          />
                        </TableCell>
                      )}
                      {columns.map((column) => (
                        <TableCell key={column.field} align={column.align}>
                          {column.renderCell ? 
                            column.renderCell(row[column.field], row) : 
                            row[column.field]
                          }
                        </TableCell>
                      ))}
                      {actions && (
                        <TableCell align="center">
                          {actions.map((action, index) => (
                            action.customRender ? (
                              // Renderizado personalizado del icono (pasando la fila completa)
                              <React.Fragment key={index}>
                                {typeof action.icon === 'function' ? action.icon(row) : action.icon}
                              </React.Fragment>
                            ) : (
                              // Renderizado estándar con IconButton
                              <Tooltip key={index} title={action.tooltip}>
                                <span> {/* Wrapper para botones deshabilitados */}
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      action.onClick(row);
                                    }}
                                    disabled={action.disabled && action.disabled(row)}
                                  >
                                    {typeof action.icon === 'function' ? action.icon(row) : action.icon}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )
                          ))}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && data.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página"
        />
      )}
    </Box>
  );
};

export default DataTable;